/* eslint-disable react/no-use-context */
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react/no-context-provider */
import { useSession, useUser } from '@clerk/clerk-react'
import { useStatsigClient } from '@statsig/react-bindings'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { isValidMapStyle } from '../components/ShipmentDetails/v3/v3ShipmentMapStyle'
import { useUserPreferencesScope } from '../hooks/useUserPreferencesScope'
import {
  getUserPreferencesGlobal,
  setUserPreferencesGlobal,
  subscribeUserPreferencesGlobal,
} from '../utility/globalPreferences'
import {
  fetchUserPreferences,
  updateMultipleUserPreferences,
  updateUserPreference,
} from '../utility/userPreferences'

function persistMapStyleLocalCache(value) {
  if (typeof localStorage === 'undefined' || !isValidMapStyle(value)) return
  localStorage.setItem('mapStyle', value)
}

const createFallbackContextValue = () => {
  const fallbackPrefs = getUserPreferencesGlobal()
  return {
    timezone: fallbackPrefs?.timezone || 'UTC',
    mapStyle: fallbackPrefs?.mapStyle || 'light',
    locale: fallbackPrefs?.locale || 'en_US',
    customColumnWidth: fallbackPrefs?.customColumnWidth || {},
    mapStyleDbValue: null,
    updatePreference: async (defaultKey, value) => {
      const nextPrefs = { ...getUserPreferencesGlobal(), [defaultKey]: value }
      setUserPreferencesGlobal(nextPrefs)
      const fallbackSession = window?.Clerk?.session || null
      const fallbackUserId = window?.Clerk?.user?.id || null
      if (fallbackSession) {
        await updateUserPreference(fallbackSession, defaultKey, value, fallbackUserId)
      }
    },
    updateMultiplePreferences: async (newPrefs) => {
      const nextPrefs = { ...getUserPreferencesGlobal(), ...newPrefs }
      setUserPreferencesGlobal(nextPrefs)
      const fallbackSession = window?.Clerk?.session || null
      const fallbackUserId = window?.Clerk?.user?.id || null
      if (fallbackSession) {
        await updateMultipleUserPreferences(fallbackSession, newPrefs, fallbackUserId)
      }
    },
    isLoading: false,
    isGateOn: false,
  }
}

const normalizePreferences = (rawPreferences = {}) => ({
  timezone: rawPreferences.timezone || 'UTC',
  mapStyle: rawPreferences.mapStyle || rawPreferences.map_style || 'light',
  locale: rawPreferences.locale || 'en_US',
  customColumnWidth: rawPreferences.customColumnWidth || rawPreferences.custom_column_width || {},
})

const UserPreferencesContext = createContext(null)

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext)

  const globalPrefs = useSyncExternalStore(
    subscribeUserPreferencesGlobal,
    getUserPreferencesGlobal,
    getUserPreferencesGlobal,
  )

  if (context == null) {
    return {
      ...createFallbackContextValue(),
      ...normalizePreferences(globalPrefs),
      mapStyleDbValue: globalPrefs?.mapStyle ?? null,
    }
  }

  return context
}

export const UserPreferencesProvider = ({ children }) => {
  const { user, isLoaded: isUserLoaded } = useUser()
  const { session, isLoaded: isSessionLoaded } = useSession()
  const { client: statsigClient } = useStatsigClient()
  const preferenceScope = useUserPreferencesScope()

  const [preferences, setPreferences] = useState(() =>
    normalizePreferences(getUserPreferencesGlobal()),
  )
  const [mapStyleDbValue, setMapStyleDbValue] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const preferencesRef = useRef(preferences)

  useEffect(() => {
    preferencesRef.current = preferences
    setUserPreferencesGlobal(preferences)
  }, [preferences])

  const loadPreferences = useCallback(async () => {
    if (!isUserLoaded || !isSessionLoaded || !user) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const supabasePrefs = await fetchUserPreferences(session, user?.id, {
        checkGate: statsigClient.checkGate.bind(statsigClient),
        orgId: preferenceScope.orgId,
      })
      if (supabasePrefs) {
        const rawMapStyle = supabasePrefs.map_style ?? supabasePrefs.mapStyle ?? null
        const normalizedPrefs = normalizePreferences(supabasePrefs)
        setMapStyleDbValue(rawMapStyle)
        setPreferences(normalizedPrefs)
        setUserPreferencesGlobal(normalizedPrefs)
        if (isValidMapStyle(rawMapStyle)) {
          persistMapStyleLocalCache(rawMapStyle)
        }
      } else {
        const rawMapStyle = user.unsafeMetadata?.mapStyle ?? null
        const normalizedPrefs = normalizePreferences({
          timezone: user.unsafeMetadata?.timezone,
          mapStyle: user.unsafeMetadata?.mapStyle,
          locale: user.unsafeMetadata?.locale,
          customColumnWidth: user.unsafeMetadata?.customColumnWidth,
        })
        setMapStyleDbValue(rawMapStyle)
        setPreferences(normalizedPrefs)
        setUserPreferencesGlobal(normalizedPrefs)
        if (isValidMapStyle(rawMapStyle)) {
          persistMapStyleLocalCache(rawMapStyle)
        }
      }
    } catch (error) {
      console.error('Error loading preferences:', error)
    } finally {
      setIsLoading(false)
    }
  }, [isUserLoaded, isSessionLoaded, user, session, statsigClient, preferenceScope.orgId])

  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  const updatePreference = useCallback(
    async (key, value) => {
      const previousPreferences = preferencesRef.current
      const previousValue = previousPreferences?.[key]
      const optimisticPrefs = { ...previousPreferences, [key]: value }
      setPreferences(optimisticPrefs)
      setUserPreferencesGlobal(optimisticPrefs)
      if (key === 'mapStyle') {
        setMapStyleDbValue(value)
        persistMapStyleLocalCache(value)
      }

      try {
        if (!isSessionLoaded || !session) {
          throw new Error(`Cannot update preference (${key}): session is not loaded`)
        }

        const didUpdateSupabase = await updateUserPreference(session, key, value, user?.id, {
          checkGate: statsigClient.checkGate.bind(statsigClient),
          orgId: preferenceScope.orgId,
        })
        if (!didUpdateSupabase) {
          throw new Error(`Supabase update failed for preference (${key})`)
        }
      } catch (error) {
        const rollbackPrefs = { ...previousPreferences, [key]: previousValue }
        setPreferences(rollbackPrefs)
        setUserPreferencesGlobal(rollbackPrefs)
        console.error(`Error updating preference ${key}:`, error)
        throw error
      }
    },
    [isSessionLoaded, session, user, statsigClient, preferenceScope.orgId],
  )

  const updateMultiplePreferences = useCallback(
    async (newPrefs) => {
      const previousPreferences = preferencesRef.current
      const optimisticPrefs = { ...previousPreferences, ...newPrefs }
      setPreferences(optimisticPrefs)
      setUserPreferencesGlobal(optimisticPrefs)
      if (Object.prototype.hasOwnProperty.call(newPrefs, 'mapStyle')) {
        const nextMapStyle = newPrefs.mapStyle
        if (isValidMapStyle(nextMapStyle)) {
          setMapStyleDbValue(nextMapStyle)
          persistMapStyleLocalCache(nextMapStyle)
        }
      }

      try {
        if (!isSessionLoaded || !session) {
          throw new Error('Cannot update multiple preferences: session is not loaded')
        }

        const didUpdateSupabase = await updateMultipleUserPreferences(session, newPrefs, user?.id, {
          checkGate: statsigClient.checkGate.bind(statsigClient),
          orgId: preferenceScope.orgId,
        })
        if (!didUpdateSupabase) {
          throw new Error('Supabase update failed for multiple preferences')
        }
      } catch (error) {
        setPreferences(previousPreferences)
        setUserPreferencesGlobal(previousPreferences)
        if (Object.prototype.hasOwnProperty.call(newPrefs, 'mapStyle')) {
          const previousMapStyle = previousPreferences.mapStyle
          if (isValidMapStyle(previousMapStyle)) {
            setMapStyleDbValue(previousMapStyle)
            persistMapStyleLocalCache(previousMapStyle)
          }
        }
        console.error('Error updating multiple preferences:', error)
        throw error
      }
    },
    [isSessionLoaded, session, user, statsigClient, preferenceScope.orgId],
  )

  const value = {
    ...preferences,
    mapStyleDbValue,
    updatePreference,
    updateMultiplePreferences,
    isLoading,
  }

  return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
}
